import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, catchError, Observable, tap } from 'rxjs';
import {
  ActivityChartData,
  ActivityDashboardSubscriptionList,
  ActivityDashboardSubscriptionQueryParams,
  ActivityDashboardWidget,
  BehaviorHeaderStats,
  ChartData,
  ChartQueryParams,
  DashboardBehaviorCaseList,
  DashboardBehaviorCaseListQueryParams,
  DistrictUsageCaseList,
  DistrictUsageCaseListQueryParams,
  DistrictUsageHeaderStats,
  DistrictUsageSummaryPayload,
  DistrictUsageSummaryResponse,
  ELearningDataList,
  ELearningDataListQueryParams,
  ELearningHeaderStats,
  Learner,
  LearnerQueryParams,
  WidgetPayload,
  WidgetReorderPayload,
  WorkshopActivity,
} from './activity-dashboard.interface';
import {
  ApiListResponse,
  ApiResponse,
  LMSAPIListResponse,
  LMSAPIResponse,
} from '../../core/models/api-response.models';
import { environment } from '../../../environments/environment.development';
import { ErrorHandlingService } from '../../core/services/error-handling.service';

@Injectable({
  providedIn: 'root',
})
export class ActivityDashboardService {
  private readonly BASE_URL = `${environment.apiUrl}/dashboard`;
  private readonly ACTIVITY_URL = `${environment.apiUrl}/dashboardactivity`;
  private readonly LMS_URL = `${environment.lmsUrl}/local/apihub/api`;
  private readonly http = inject(HttpClient);
  private readonly errorHandler = inject(ErrorHandlingService);

  // --- Subjects & Observables ---
  dateRangeSubject = new BehaviorSubject<Date[]>([]);
  dateRange$ = this.dateRangeSubject.asObservable();

  behaviorHederStatSubject =
    new BehaviorSubject<ApiResponse<BehaviorHeaderStats> | null>(null);
  behvaiorHeaderStats$ = this.behaviorHederStatSubject.asObservable();

  districUsageHeaderStatSubject =
    new BehaviorSubject<ApiResponse<DistrictUsageHeaderStats> | null>(null);
  distrciUsageHeaderStat$ = this.districUsageHeaderStatSubject.asObservable();

  districUsageSummarySubject =
    new BehaviorSubject<DistrictUsageSummaryResponse | null>(null);
  distrciUsageHeaderSummary$ = this.districUsageSummarySubject.asObservable();

  eLearningSubject =
    new BehaviorSubject<LMSAPIResponse<ELearningHeaderStats> | null>(null);
  eLearningHeaderSummary$ = this.eLearningSubject.asObservable();

  behaviorCaseListSubject =
    new BehaviorSubject<ApiListResponse<DashboardBehaviorCaseList> | null>(
      null
    );
  behvaiorCaseList$ = this.behaviorCaseListSubject.asObservable();

  districUasageCaseListSubject =
    new BehaviorSubject<ApiListResponse<DistrictUsageCaseList> | null>(null);
  districUsageCaseList$ = this.districUasageCaseListSubject.asObservable();

  eLEarningListSubject =
    new BehaviorSubject<LMSAPIListResponse<ELearningDataList> | null>(null);
  eLearningList$ = this.eLEarningListSubject.asObservable();

  private orgnizationSubject = new BehaviorSubject<number>(0);
  orgnizationId$ = this.orgnizationSubject.asObservable();

  userSubject = new BehaviorSubject<number>(0);
  userId$ = this.userSubject.asObservable();

  studentSubject = new BehaviorSubject<number>(0);
  studentId$ = this.studentSubject.asObservable();

  learnerSubject = new BehaviorSubject<number>(0);
  leanerId$ = this.learnerSubject.asObservable();

  private dashboardSubject = new BehaviorSubject<number>(0);
  dashboardId$ = this.dashboardSubject.asObservable();

  widgetSubject = new BehaviorSubject<ActivityDashboardWidget[] | null>(null);
  widgets$ = this.widgetSubject.asObservable();

  districSubscriptionListSubject =
    new BehaviorSubject<ApiListResponse<ActivityDashboardSubscriptionList> | null>(
      null
    );
  districSubscriptionList$ = this.districSubscriptionListSubject.asObservable();

  // --- State Update Methods ---
  updateDateRange(dateRange: Date[]) {
    this.dateRangeSubject.next(dateRange);
  }

  updateValue(newValue: number) {
    this.orgnizationSubject.next(newValue);
  }

  updateDashboardId(newValue: number) {
    this.dashboardSubject.next(newValue);
  }

  getDashboardWidgets(
    userId: number,
    dashboardType: number
  ): Observable<ApiResponse<ActivityDashboardWidget[]>> {
    return this.http
      .get<
        ApiResponse<ActivityDashboardWidget[]>
      >(`${this.BASE_URL}/widget?userId=${userId}&dashbaoardType=${dashboardType}`)
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  updateWidgetStatus(payload: WidgetPayload) {
    return this.http
      .put<
        ApiResponse<ActivityDashboardWidget>
      >(`${this.BASE_URL}/widget`, payload)
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }
  updateWidgetOrder(
    userId: number,
    dashboardType: number,
    payload: WidgetReorderPayload[]
  ) {
    return this.http
      .put<
        ApiResponse<ActivityDashboardWidget[]>
      >(`${this.BASE_URL}/widget-sequence?userId=${userId}&dashboardType=${dashboardType}`, payload)
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getBehaviorOverviewChartData(
    payload: ChartQueryParams
  ): Observable<ApiResponse<ChartData[]>> {
    const params = this.buildQueryParams(payload);
    return this.http
      .get<
        ApiResponse<ChartData[]>
      >(`${this.BASE_URL}/behavior-managment-overview/chart`, { params })
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  buildQueryParams(payload: ChartQueryParams): HttpParams {
    let param = new HttpParams();
    if (payload.ChartType) {
      param = param.set('ChartType', payload.ChartType);
    }
    if (payload.OrganizationId && payload.OrganizationId !== 0) {
      param = param.set('OrganizationId', payload.OrganizationId);
    }
    if (payload.OrganizationIds && payload.OrganizationIds.length > 0) {
      payload.OrganizationIds.forEach(orgId => {
        if (orgId !== null && orgId !== undefined && orgId !== 0) {
          param = param.append('OrganizationIds', orgId.toString());
        }
      });
    }
    if (payload.UserSearch) {
      param = param.set('UserSearch', payload.UserSearch);
    }
    if (payload.StudentSearch) {
      param = param.set('StudentSearch', payload.StudentSearch);
    }
    if (payload.StartDate) {
      param = param.set('StartDate', payload.StartDate);
    }
    if (payload.EndDate) {
      param = param.set('EndDate', payload.EndDate);
    }
    return param;
  }

  getBehaviorHeaderStats(
    payload: ChartQueryParams
  ): Observable<ApiResponse<BehaviorHeaderStats>> {
    const params = this.buildQueryParams(payload);
    return this.http
      .get<
        ApiResponse<BehaviorHeaderStats>
      >(`${this.BASE_URL}/behavior-managment-overview/header-stats`, { params })
      .pipe(
        tap(response => this.behaviorHederStatSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getBehvaiorOverviewCaseList(payload: DashboardBehaviorCaseListQueryParams) {
    const params = this.buildCaseListQueryParams(payload);
    return this.http
      .get<
        ApiListResponse<DashboardBehaviorCaseList>
      >(`${this.BASE_URL}/behavior-managment-overview/case-list`, { params })
      .pipe(
        tap(response => this.behaviorCaseListSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  buildCaseListQueryParams(
    payload: DashboardBehaviorCaseListQueryParams
  ): HttpParams {
    let param = new HttpParams();
    if (payload.OrganizationId) {
      param = param.set('OrganizationId', payload.OrganizationId);
    }
    if (payload.UserSearch) {
      param = param.set('UserSearch', payload.UserSearch);
    }
    if (payload.StudentSearch) {
      param = param.set('StudentSearch', payload.StudentSearch);
    }
    if (payload.StartDate) {
      param = param.set('StartDate', payload.StartDate);
    }
    if (payload.EndDate) {
      param = param.set('EndDate', payload.EndDate);
    }
    if (payload.PageSize) {
      param = param.set('PageSize', payload.PageSize);
    }
    if (payload.Page) {
      param = param.set('Page', payload.Page);
    }
    if (payload.Sort) {
      param = param.set('Sort', payload.Sort);
    }
    if (payload.SortOrder !== undefined) {
      param = param.set('SortOrder', payload.SortOrder);
    }
    if (payload.StudentName) {
      param = param.set('StudentName', payload.StudentName);
    }
    if (payload.School) {
      param = param.set('School', payload.School);
    }
    if (payload.StudentId) {
      param = param.set('StudentId', payload.StudentId);
    }
    if (payload.DOB) {
      param = param.set('DOB', payload.DOB);
    }
    if (payload.Age) {
      param = param.set('Age', payload.Age);
    }

    return param;
  }

  getDistricUsageHeaderStats(
    payload: ChartQueryParams
  ): Observable<ApiResponse<DistrictUsageHeaderStats>> {
    const params = this.buildQueryParams(payload);
    return this.http
      .get<
        ApiResponse<DistrictUsageHeaderStats>
      >(`${this.BASE_URL}/district-usage/header-stats`, { params })
      .pipe(
        tap(response => this.districUsageHeaderStatSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  buildQueryParamsForDistrictUsage(
    payload: DistrictUsageSummaryPayload
  ): DistrictUsageSummaryPayload {
    const body: DistrictUsageSummaryPayload = {
      orgid: payload.orgid,
      startdate: payload.startdate,
      enddate: payload.enddate,
      type: payload.type,
    };
    return body;
  }
  getDistricUsageChartData(
    payload: ChartQueryParams
  ): Observable<ApiResponse<ChartData[]>> {
    const params = this.buildQueryParams(payload);
    return this.http
      .get<
        ApiResponse<ChartData[]>
      >(`${this.BASE_URL}/district-usage/chart`, { params })
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getDistricUsageLMSChartData(
    payload: DistrictUsageSummaryPayload
  ): Observable<LMSAPIResponse<ChartData[]>> {
    const body = this.buildQueryParamsForDistrictUsage(payload);
    return this.http
      .post<
        LMSAPIResponse<ChartData[]>
      >(`${this.LMS_URL}/dashboard/e_learning_implementation.php`, body)
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getDistricUsageCaseList(payload: DistrictUsageCaseListQueryParams) {
    const params = this.buildCaseListQueryParams(payload);
    return this.http
      .get<
        ApiListResponse<DistrictUsageCaseList>
      >(`${this.BASE_URL}/district-usage/case-list`, { params })
      .pipe(
        tap(response => this.districUasageCaseListSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getDistricUsageSummary(payload: DistrictUsageSummaryPayload) {
    const body: DistrictUsageSummaryPayload = {
      orgid: payload.orgid,
      startdate: payload.startdate,
      enddate: payload.enddate,
      type: payload.type,
    };
    return this.http

      .post<DistrictUsageSummaryResponse>(
        `${this.LMS_URL}/dashboard/e_learning_implementation.php`,
        body
      )
      .pipe(
        tap(response => this.districUsageSummarySubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getLearnerList(orgId: number) {
    // Remove token usage - authentication handled by cookies
    const body: LearnerQueryParams = {
      // jwt: token || '', // Remove this line
      orgid: orgId,
    };
    return this.http
      .post<
        LMSAPIResponse<Learner[]>
      >(`${this.LMS_URL}/reports/get_org_learners.php`, body, { withCredentials: true })
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getELearingSummary(payload: DistrictUsageSummaryPayload) {
    // Remove token usage - authentication handled by cookies
    const body: DistrictUsageSummaryPayload = {
      // jwt: token || '', // Remove this line
      orgid: payload.orgid,
      startdate: payload.startdate,
      enddate: payload.enddate,
      type: payload.type,
      userid: payload.userid,
    };
    return this.http

      .post<
        LMSAPIResponse<ELearningHeaderStats>
      >(`${this.LMS_URL}/dashboard/e_learning_implementation.php`, body, { withCredentials: true })
      .pipe(
        tap(response => this.eLearningSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }
  getELearingDataList(payload: ELearningDataListQueryParams) {
    // Remove token usage - authentication handled by cookies
    const body: ELearningDataListQueryParams = {
      // jwt: token || '', // Remove this line
      startdate: payload.startdate,
      enddate: payload.enddate,
      page: payload.page,
      perpage: payload.perpage,
      orderBy: payload.orderBy,
      orderByType: payload.orderByType,
    };
    if (payload.orgid) {
      body.orgid = payload.orgid;
    }
    if (payload.userid) {
      body.userid = payload.userid;
    }
    return this.http

      .post<
        LMSAPIListResponse<ELearningDataList>
      >(`${this.LMS_URL}/dashboard/e_learning_implementation_details.php`, body, { withCredentials: true })
      .pipe(
        tap(response => this.eLEarningListSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getActivityDashBoardData(payload: DistrictUsageSummaryPayload) {
    // Remove token usage - authentication handled by cookies
    const body: DistrictUsageSummaryPayload = {
      // jwt: token || '', // Remove this line
      orgid: payload.orgid,
      startdate: payload.startdate,
      enddate: payload.enddate,
      type: payload.type,
    };
    if (payload.userid && payload.userid !== 0) {
      body.userid = payload.userid;
    }
    return this.http
      .post<
        LMSAPIResponse<ChartData[] | WorkshopActivity[]>
      >(`${this.LMS_URL}/dashboard/activity_dashboard.php`, body, { withCredentials: true })
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getActivityDashBoardChartData(payload: ChartQueryParams) {
    const params = this.buildQueryParams(payload);
    return this.http
      .get<
        ApiResponse<ActivityChartData>
      >(`${this.ACTIVITY_URL}/chart`, { params })
      .pipe(
        tap(response => response),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  getActivitySubscriptionList(
    payload: ActivityDashboardSubscriptionQueryParams
  ) {
    const params = this.buildSubscriptionParams(payload);
    return this.http
      .get<
        ApiListResponse<ActivityDashboardSubscriptionList>
      >(`${this.ACTIVITY_URL}/district-subscription-list`, { params })
      .pipe(
        tap(response => this.districSubscriptionListSubject.next(response)),
        catchError(error => this.errorHandler.handleError(error))
      );
  }

  buildSubscriptionParams(payload: ActivityDashboardSubscriptionQueryParams) {
    let param = new HttpParams();
    if (payload.PageSize) {
      param = param.set('PageSize', payload.PageSize);
    }
    if (payload.Page) {
      param = param.set('Page', payload.Page);
    }
    if (payload.Sort) {
      param = param.set('Sort', payload.Sort);
    }
    if (payload.SortOrder) {
      param = param.set('SortOrder', payload.SortOrder);
    }
    if (payload.OrganizationIds && payload.OrganizationIds.length > 0) {
      payload.OrganizationIds.forEach(orgId => {
        if (orgId !== null && orgId !== undefined && orgId !== 0) {
          param = param.append('OrganizationIds', orgId.toString());
        }
      });
    }
    return param;
  }
}
